import React from 'react';
import { Link } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import LazyAvatar from '../Avatar/LazyAvatar/LazyAvatar';
import Button from '../Button/Button';
import SvgIcon from '../Icons';

const CARD_HEIGHT = 510;
const BORDER_RING = '#0a1f06';

type UpsellTarget = 'starter' | 'plus';

type UpsellAvatar = {
  src?: string;
  placeholderSrc?: string;
  alt?: string;
};

type UpsellPost = {
  src?: string;
  placeholderSrc?: string;
  videoSrc?: string;
};

type UpsellKind = 'brand' | 'venue';

type PromotionUpsellCardProps = {
  target: UpsellTarget;
  pendingCount: number;
  avatars?: UpsellAvatar[];
  posts?: UpsellPost[];
  daysUntilReset?: number;
  kind?: UpsellKind;
};

type PaletteEntry = {
  spin: string;
  buttonGlow: string;
  haloA: string;
  haloB: string;
  bg: string;
  ring: string;
  buttonBg: string;
  buttonHoverBg: string;
  buttonShadow: string;
  iconFill: string;
};

const palette: Record<UpsellTarget, PaletteEntry> = {
  starter: {
    spin:
      'conic-gradient(from 0deg, transparent 0%, transparent 48%, #9BE86B 66%, #2FA600 84%, transparent 100%)',
    buttonGlow:
      'conic-gradient(from 0deg, transparent 0%, transparent 70%, #C7F5A0 82%, #E8FBD2 88%, transparent 96%)',
    haloA: 'radial-gradient(circle, rgba(155, 232, 107, 0.45) 0%, rgba(155, 232, 107, 0) 70%)',
    haloB: 'radial-gradient(circle, rgba(47, 166, 0, 0.4) 0%, rgba(47, 166, 0, 0) 70%)',
    bg: 'linear-gradient(135deg, #1f4d12 0%, #102b09 55%, #060f04 100%)',
    ring: '#0a1f06',
    buttonBg: 'linear-gradient(180deg, #429C12 0%, #2F7B0A 100%)',
    buttonHoverBg: 'linear-gradient(180deg, #4FB218 0%, #3A8E10 100%)',
    buttonShadow: '0 0 16px rgba(199, 245, 160, 0.75)',
    iconFill: '#ffffff',
  },
  plus: {
    spin:
      'conic-gradient(from 0deg, transparent 0%, transparent 48%, #E0A3FF 66%, #FF6FD8 84%, transparent 100%)',
    buttonGlow:
      'conic-gradient(from 0deg, transparent 0%, transparent 70%, #F5D0FF 82%, #FBE7FF 88%, transparent 96%)',
    haloA: 'radial-gradient(circle, rgba(224, 163, 255, 0.45) 0%, rgba(224, 163, 255, 0) 70%)',
    haloB: 'radial-gradient(circle, rgba(255, 111, 216, 0.4) 0%, rgba(255, 111, 216, 0) 70%)',
    bg: 'linear-gradient(135deg, #4a1444 0%, #2a0a26 55%, #0d050b 100%)',
    ring: '#1f0a1d',
    buttonBg: 'linear-gradient(180deg, #D86EEF 0%, #B855CC 100%)',
    buttonHoverBg: 'linear-gradient(180deg, #E083F5 0%, #C566DA 100%)',
    buttonShadow: '0 0 16px rgba(245, 208, 255, 0.75)',
    iconFill: '#ffffff',
  },
};

export const PromotionUpsellCard: React.FC<PromotionUpsellCardProps> = ({
  target,
  pendingCount,
  avatars = [],
  posts = [],
  daysUntilReset,
  kind = 'brand',
}) => {
  const showAvatars = pendingCount > 3 && avatars.length > 0;
  const showPosts = !showAvatars && posts.length > 0;
  const visibleAvatars = avatars.slice(0, 3);
  const visiblePosts = posts.slice(0, 3);

  const planLabel = target === 'plus' ? 'Plus' : 'Starter';
  const ctaLabel = `Upgrade to ${planLabel}`;

  const visibilityPrefix =
    daysUntilReset != null && daysUntilReset > 0
      ? `Don't lose ${daysUntilReset} day${daysUntilReset === 1 ? '' : 's'} of visibility. `
      : '';

  let title: string;
  let descriptionLines: string[];

  if (showAvatars) {
    title = `${pendingCount} users are waiting to promote your ${kind}`;
    descriptionLines = [
      target === 'plus'
        ? 'Upgrade to Plus to receive unlimited promotions every month.'
        : 'Upgrade to Starter to keep receiving promotions from real users.',
    ];
  } else {
    title = 'Your brand is getting attention right now';
    descriptionLines = [
      `${visibilityPrefix}Upgrade to unlock up to 5x more promotions and stay active while demand is high`,
    ];
  }

  const { haloA, haloB, bg, ring, buttonGlow, buttonBg, buttonHoverBg, iconFill } =
    palette[target];

  const postBaseOffsets = [-6, 6, -10];
  const postRotations = [-5, 3, -4];

  return (
    <Box
      sx={{
        position: 'relative',
        width: 'calc((100% - 48px) / 4)',
        minWidth: 320,
        minHeight: CARD_HEIGHT,
        fontFamily: 'Poppins',
      }}
    >
      {/* Main card (no external glow on the card itself) */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          borderRadius: '16px',
          overflow: 'hidden',
          background: bg,
          border: `1px solid ${ring}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            zIndex: 0,
            top: '-30%',
            left: '-30%',
            width: '420px',
            height: '420px',
            background: haloA,
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            zIndex: 0,
            bottom: '-30%',
            right: '-30%',
            width: '420px',
            height: '420px',
            background: haloB,
            pointerEvents: 'none',
          },
        }}
      >
        {/* Bottom black gradient overlay (taller, softer fade at top, 80% max) */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            background:
              'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.08) 15%, rgba(0,0,0,0.25) 30%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.7) 75%, rgba(0,0,0,0.8) 100%)',
          }}
        />
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2.5,
            p: 3,
            pb: '90px',
          }}
        >
          {showAvatars ? (
            <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
              {visibleAvatars.map((avatar, index) => (
                <LazyAvatar
                  key={index}
                  variant='circular'
                  src={avatar.src}
                  placeholderSrc={avatar.placeholderSrc}
                  alt={avatar.alt}
                  sx={{
                    width: 56,
                    height: 56,
                    border: `3px solid ${BORDER_RING}`,
                    ml: index === 0 ? 0 : '-18px',
                  }}
                />
              ))}
              {pendingCount > visibleAvatars.length && (
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    ml: '-18px',
                    borderRadius: '50%',
                    border: `3px solid ${BORDER_RING}`,
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  +{pendingCount - visibleAvatars.length}
                </Box>
              )}
            </Box>
          ) : showPosts ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: '-10px',
                width: 'calc(100% + 20px)',
                pt: 1,
                pb: 1,
              }}
            >
              {visiblePosts.map((post, index) => {
                const baseY = postBaseOffsets[index] ?? 0;
                const rot = postRotations[index] ?? 0;
                return (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      flexShrink: 0,
                      width: 120,
                      height: 188,
                      borderRadius: '16px',
                      overflow: 'hidden',
                      border: `2px solid ${BORDER_RING}`,
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      ml: index === 0 ? 0 : '-22px',
                      zIndex: index === 1 ? 2 : 1,
                      transform: `translateY(${baseY}px) rotate(${rot}deg)`,
                      transformOrigin: 'center',
                    }}
                  >
                    {post.videoSrc ? (
                      <Box
                        component='video'
                        src={post.videoSrc}
                        autoPlay
                        loop
                        muted
                        playsInline
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          backgroundImage: post.src ? `url(${post.src})` : undefined,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                flexShrink: 0,
                width: 56,
                height: 56,
                borderRadius: '14px',
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SvgIcon name='Plus' sx={{ fontSize: '30px', fill: '#1a1a1a' }} />
            </Box>
          )}

          <Box sx={{ mt: 'auto' }}>
            <Typography sx={{ color: '#ffffff', fontSize: '20px', fontWeight: 600, mb: 1 }}>
              {title}
            </Typography>
            {descriptionLines.map((line, i) => (
              <Typography
                key={i}
                sx={{
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: 1.5,
                  mt: i === 0 ? 0 : 1,
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* CTA with its own external animated glow — aligned with text padding */}
        <Box
          sx={{
            position: 'absolute',
            zIndex: 3,
            left: 24,
            right: 24,
            bottom: 24,
          }}
        >
          <Box sx={{ position: 'relative' }}>
            {/* Spinning glow around the button — tight and defined */}
            <Box
              sx={{
                position: 'absolute',
                inset: '-2px',
                zIndex: 0,
                borderRadius: '10px',
                overflow: 'hidden',
                pointerEvents: 'none',
                filter: 'blur(1.5px)',
                opacity: 0.95,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 480,
                  height: 480,
                  transform: 'translate(-50%, -50%)',
                  background: buttonGlow,
                  animation: 'upsellButtonGlowSpin 4.5s linear infinite',
                  '@keyframes upsellButtonGlowSpin': {
                    to: { transform: 'translate(-50%, -50%) rotate(360deg)' },
                  },
                }}
              />
            </Box>

            <Button
              component={Link}
              to='/plans'
              variant='contained'
              size='large'
              fullWidth
              startIcon={
                <SvgIcon name='Plus' sx={{ fontSize: '20px', fill: iconFill }} />
              }
              sx={{
                position: 'relative',
                zIndex: 1,
                fontWeight: 600,
                color: '#ffffff',
                background: buttonBg,
                boxShadow: 'none',
                '&:hover': {
                  background: buttonHoverBg,
                  boxShadow: 'none',
                },
              }}
            >
              {ctaLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PromotionUpsellCard;
